import React, { useEffect, useState } from "react";
import {
    collection,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    Timestamp,
    query,
    where,
    orderBy
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { toast } from "react-toastify";
import Loader from "./Loader";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

export default function ExpenseTracker() {
    const COLLECTION_NAME = "EXPENSES";
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [filtered, setFiltered] = useState([]);

    // Filters
    const [search, setSearch] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [category, setCategory] = useState("ALL");
    const [typeFilter, setTypeFilter] = useState("ALL"); // Income or Expense

    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [showForm, setShowForm] = useState(false);
    const [editId, setEditId] = useState(null);

    // Multiple Books Support
    const [selectedBook, setSelectedBook] = useState(localStorage.getItem('expense_book') || "Main Book");
    const [availableBooks, setAvailableBooks] = useState(["Main Book"]);
    const [showAddBook, setShowAddBook] = useState(false);
    const [showEditBook, setShowEditBook] = useState(false);
    const [newBookName, setNewBookName] = useState("");
    const [editBookName, setEditBookName] = useState("");

    const [form, setForm] = useState({
        date: new Date().toISOString().split('T')[0],
        title: "",
        category: "General",
        amount: "",
        type: "Expense", // Income or Expense
        mode: "Cash", // Cash, Online, Cheque
        remarks: ""
    });

    const categories = ["General", "Food", "Travel", "Office", "Salary", "Rent", "Utilities", "Other"];

    /* ---------------- LOAD DATA ---------------- */
    const loadBooks = async () => {
        try {
            const snap = await getDocs(collection(db, COLLECTION_NAME));
            const dbBooks = snap.docs.map(d => d.data().book).filter(Boolean);
            // Ensure Main Book and current selection are always in the list
            const books = ["Main Book", selectedBook, ...dbBooks];
            setAvailableBooks([...new Set(books)]);
        } catch (err) {
            console.error("Error loading books:", err);
        }
    };

    const loadData = async () => {
        try {
            setLoading(true);
            const q = query(
                collection(db, COLLECTION_NAME),
                where("book", "==", selectedBook),
                orderBy("date", "desc")
            );
            const snap = await getDocs(q);
            const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setData(rows);
            setFiltered(rows);
        } catch (err) {
            console.error("Error loading expenses (Query failed - checking for index or empty):", err);
            // Fallback for missing index or first load
            const qBasic = query(collection(db, COLLECTION_NAME), orderBy("date", "desc"));
            const snap = await getDocs(qBasic);
            const allRows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            const filteredRows = allRows.filter(r => (r.book || "Main Book") === selectedBook);
            setData(filteredRows);
            setFiltered(filteredRows);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadBooks();
        loadData();
    }, [selectedBook]);

    /* ---------------- FILTERS ---------------- */
    useEffect(() => {
        let r = [...data];

        if (search) {
            const q = search.toLowerCase();
            r = r.filter(row =>
                (row.title || "").toLowerCase().includes(q) ||
                (row.remarks || "").toLowerCase().includes(q)
            );
        }

        if (fromDate) {
            const fd = new Date(fromDate);
            fd.setHours(0, 0, 0, 0);
            r = r.filter(row => row.date.toDate() >= fd);
        }

        if (toDate) {
            const td = new Date(toDate);
            td.setHours(23, 59, 59, 999);
            r = r.filter(row => row.date.toDate() <= td);
        }

        if (category !== "ALL") {
            r = r.filter(row => row.category === category);
        }

        if (typeFilter !== "ALL") {
            r = r.filter(row => row.type === typeFilter);
        }

        setFiltered(r);
        setPage(1);
    }, [search, fromDate, toDate, category, typeFilter, data]);

    /* ---------------- ACTIONS ---------------- */
    const saveEntry = async () => {
        if (!form.title || !form.amount || !form.date) {
            alert("Please fill required fields (Date, Title, Amount)");
            return;
        }

        const payload = {
            ...form,
            book: selectedBook,
            amount: Number(form.amount),
            date: Timestamp.fromDate(new Date(form.date)),
            createdAt: new Date()
        };

        setLoading(true);
        try {
            if (editId) {
                await updateDoc(doc(db, COLLECTION_NAME, editId), payload);
            } else {
                await addDoc(collection(db, COLLECTION_NAME), payload);
            }
            setShowForm(false);
            setEditId(null);
            loadData();
        } catch (err) {
            console.error("Error saving entry:", err);
            alert("Failed to save entry");
        } finally {
            setLoading(false);
        }
    };

    const deleteEntry = async (id) => {
        if (!window.confirm("Delete this entry?")) return;
        setLoading(true);
        try {
            await deleteDoc(doc(db, COLLECTION_NAME, id));
            loadData();
        } catch (err) {
            console.error("Error deleting:", err);
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- BOOK MANAGEMENT ---------------- */
    const handleRenameBook = async () => {
        if (!editBookName.trim() || editBookName === selectedBook) return;
        const newName = editBookName.trim();

        if (window.confirm(`Rename book "${selectedBook}" to "${newName}"? This will update all existing entries.`)) {
            setLoading(true);
            try {
                const q = query(collection(db, COLLECTION_NAME), where("book", "==", selectedBook));
                const snap = await getDocs(q);

                const updates = snap.docs.map(d => updateDoc(doc(db, COLLECTION_NAME, d.id), { book: newName }));
                await Promise.all(updates);

                setSelectedBook(newName);
                localStorage.setItem('expense_book', newName);
                setShowEditBook(false);
                loadBooks();
                loadData();
                toast.success("Book renamed successfully");
            } catch (err) {
                console.error("Rename failed:", err);
                toast.error("Failed to rename book");
            } finally {
                setLoading(false);
            }
        }
    };

    const handleDeleteBook = async (bookName) => {
        if (bookName === "Main Book") return alert("Main Book cannot be deleted.");

        if (window.confirm(`Are you sure you want to DELETE the entire book "${bookName}" and ALL its entries? This action cannot be undone.`)) {
            setLoading(true);
            try {
                const q = query(collection(db, COLLECTION_NAME), where("book", "==", bookName));
                const snap = await getDocs(q);

                const deletes = snap.docs.map(d => deleteDoc(doc(db, COLLECTION_NAME, d.id)));
                await Promise.all(deletes);

                setSelectedBook("Main Book");
                localStorage.setItem('expense_book', "Main Book");
                loadBooks();
                loadData();
                toast.success("Book and all entries deleted");
            } catch (err) {
                console.error("Delete failed:", err);
                toast.error("Failed to delete book");
            } finally {
                setLoading(false);
            }
        }
    };

    // Export
    const exportToExcel = () => {
        if (!filtered.length) return alert("No data to export");
        const ws = XLSX.utils.json_to_sheet(filtered.map(r => ({
            Date: r.date.toDate().toLocaleDateString(),
            Title: r.title,
            Category: r.category,
            Type: r.type,
            Mode: r.mode,
            Amount: r.amount,
            Remarks: r.remarks
        })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Expenses");
        XLSX.writeFile(wb, `Expenses_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    /* ---------------- TOTALS ---------------- */
    const totalIncome = filtered.filter(r => r.type === 'Income').reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const totalExpense = filtered.filter(r => r.type === 'Expense').reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const netBalance = totalIncome - totalExpense;

    /* ---------------- PAGINATION ---------------- */
    const startIndex = (page - 1) * rowsPerPage;
    const pagedRows = filtered.slice(startIndex, startIndex + rowsPerPage);

    const formatDate = (ts) => {
        if (!ts) return "-";
        return ts.toDate().toLocaleDateString();
    };

    if (loading) {
  return (
      <Loader />
  );
}

    return (
        <div className="mt-4">

            <div className="d-flex align-items-center gap-3 mb-4 bg-light p-3 rounded shadow-sm">
                <div className="fw-bold text-primary"><i className="bi bi-journal-bookmark-fill me-2"></i>Expense Book:</div>
                <div className="d-flex gap-2 flex-wrap">
                    {availableBooks.map(b => (
                        <div key={b} className="d-flex align-items-center bg-white rounded border overflow-hidden">
                            <button
                                className={`btn btn-sm border-0 rounded-0 ${selectedBook === b ? 'btn-primary' : 'btn-outline-primary'}`}
                                style={{ fontWeight: selectedBook === b ? 'bold' : 'normal' }}
                                onClick={() => {
                                    setSelectedBook(b);
                                    localStorage.setItem('expense_book', b);
                                }}
                            >
                                {b}
                            </button>
                            {selectedBook === b && b !== "Main Book" && (
                                <div className="d-flex bg-primary bg-opacity-10">
                                    <button
                                        className="btn btn-sm text-primary px-2 border-0"
                                        title="Rename Book"
                                        onClick={() => { setEditBookName(b); setShowEditBook(true); }}
                                    >
                                        <i className="bi bi-pencil"></i>
                                    </button>
                                    <button
                                        className="btn btn-sm text-danger px-2 border-0"
                                        title="Delete Book"
                                        onClick={() => handleDeleteBook(b)}
                                    >
                                        <i className="bi bi-trash"></i>
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowAddBook(true)}>
                        <i className="bi bi-plus-lg"></i> Add New Book
                    </button>
                </div>
            </div>

            {/* SUMMARY TILES */}
            <div className="row text-center mb-4">
                <div className="col-md-4 mb-2">
                    <div className="card border-success bg-success bg-opacity-10 p-3">
                        <h6 className="text-success fw-bold">Cash In (Income)</h6>
                        <h3>₹ {totalIncome.toLocaleString()}</h3>
                    </div>
                </div>
                <div className="col-md-4 mb-2">
                    <div className="card border-danger bg-danger bg-opacity-10 p-3">
                        <h6 className="text-danger fw-bold">Cash Out (Expense)</h6>
                        <h3>₹ {totalExpense.toLocaleString()}</h3>
                    </div>
                </div>
                <div className="col-md-4 mb-2">
                    <div className="card border-primary bg-primary bg-opacity-10 p-3">
                        <h6 className="text-primary fw-bold">Net Balance</h6>
                        <h3>₹ {netBalance.toLocaleString()}</h3>
                    </div>
                </div>
            </div>

            {/* CONTROLS */}
            <div className="d-flex flex-wrap gap-2 justify-content-between mb-3">
                <div className="d-flex gap-2 flex-wrap">
                    <input className="form-control" style={{ width: '200px' }} placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
                    <input type="date" className="form-control" style={{ width: '150px' }} value={fromDate} onChange={e => setFromDate(e.target.value)} />
                    <input type="date" className="form-control" style={{ width: '150px' }} value={toDate} onChange={e => setToDate(e.target.value)} />
                    <select className="form-select" style={{ width: '150px' }} value={category} onChange={e => setCategory(e.target.value)}>
                        <option value="ALL">All Categories</option>
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select className="form-select" style={{ width: '150px' }} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                        <option value="ALL">All Types</option>
                        <option value="Income">Cash In</option>
                        <option value="Expense">Cash Out</option>
                    </select>
                </div>
                <div>
                    <button className="btn btn-success me-2" onClick={exportToExcel}>Export</button>
                    <button className="btn btn-primary" onClick={() => {
                        setEditId(null);
                        setForm({
                            date: new Date().toISOString().split('T')[0],
                            title: "",
                            category: "General",
                            amount: "",
                            type: "Expense",
                            mode: "Cash",
                            remarks: ""
                        });
                        setShowForm(true);
                    }}>+ Add Entry</button>
                </div>
            </div>

            {/* TABLE */}
            <div className="table-responsive">
                <table className="table table-bordered table-hover align-middle">
                    <thead className="table-dark">
                        <tr>
                            <th>Date</th>
                            <th>Details</th>
                            <th>Category</th>
                            <th>Mode</th>
                            <th className="text-end">Amount</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pagedRows.length === 0 ? (
                            <tr><td colSpan="6" className="text-center">No entries found</td></tr>
                        ) : (
                            pagedRows.map(r => (
                                <tr key={r.id}>
                                    <td>{formatDate(r.date)}<br /><small className="text-muted">{new Date(r.date.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</small></td>
                                    <td>{r.title}
                                        {r.remarks && <div className="text-muted small">{r.remarks}</div>}
                                    </td>
                                    <td><span className="badge bg-secondary">{r.category}</span></td>
                                    <td>{r.mode}</td>
                                    <td className={`text-end fw-bold ${r.type === 'Income' ? 'text-success' : 'text-danger'}`}>
                                        {r.type === 'Income' ? '+' : '-'} ₹ {Number(r.amount).toLocaleString()}
                                    </td>
                                    <td>
                                        <button className="btn btn-sm btn-outline-primary me-1" onClick={() => {
                                            setEditId(r.id);
                                            setForm({
                                                ...r,
                                                date: new Date(r.date.seconds * 1000).toISOString().split('T')[0]
                                            });
                                            setShowForm(true);
                                        }}>Edit</button>
                                        <button className="btn btn-sm btn-outline-danger" onClick={() => deleteEntry(r.id)}>Delete</button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* PAGINATION */}
            {filtered.length > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                    <small className="text-muted">Showing {startIndex + 1}-{Math.min(startIndex + rowsPerPage, filtered.length)} of {filtered.length}</small>
                    <div className="d-flex gap-2">
                        <button className="btn btn-sm btn-outline-secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
                        <button className="btn btn-sm btn-outline-secondary" disabled={page === Math.ceil(filtered.length / rowsPerPage)} onClick={() => setPage(page + 1)}>Next</button>
                    </div>
                </div>
            )}

            {/* MODAL */}
            {showForm && (
                <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{editId ? 'Edit Entry' : 'New Entry'}</h5>
                                <button className="btn-close" onClick={() => setShowForm(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Type</label>
                                    <div className="d-flex gap-3">
                                        <div className="form-check">
                                            <input className="form-check-input" type="radio" name="type" checked={form.type === 'Income'} onChange={() => setForm({ ...form, type: 'Income' })} />
                                            <label className="form-check-label text-success fw-bold">Cash In (Income)</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="radio" name="type" checked={form.type === 'Expense'} onChange={() => setForm({ ...form, type: 'Expense' })} />
                                            <label className="form-check-label text-danger fw-bold">Cash Out (Expense)</label>
                                        </div>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label>Date</label>
                                    <input type="date" className="form-control" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
                                </div>
                                <div className="mb-3">
                                    <label>Title / Details</label>
                                    <input type="text" className="form-control" placeholder="e.g. Office Rent, Tea" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
                                </div>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label>Category</label>
                                        <select className="form-select" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                                            {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label>Mode</label>
                                        <select className="form-select" value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value })}>
                                            <option>Cash</option>
                                            <option>Online</option>
                                            <option>Cheque</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label>Amount</label>
                                    <input type="number" className="form-control" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
                                </div>
                                <div className="mb-3">
                                    <label>Remarks</label>
                                    <textarea className="form-control" rows="2" value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })}></textarea>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={saveEntry}>Save</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ADD BOOK MODAL */}
            {showAddBook && (
                <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-sm modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg">
                            <div className="modal-header">
                                <h6 className="modal-title fw-bold">Create New Expense Book</h6>
                                <button className="btn-close" onClick={() => setShowAddBook(false)}></button>
                            </div>
                            <div className="modal-body p-3">
                                <input
                                    className="form-control mb-3"
                                    placeholder="Book Name (e.g. Factory)"
                                    value={newBookName}
                                    onChange={e => setNewBookName(e.target.value)}
                                />
                                <div className="d-grid">
                                    <button className="btn btn-primary" onClick={() => {
                                        if (!newBookName.trim()) return;
                                        const name = newBookName.trim();
                                        if (!availableBooks.includes(name)) {
                                            setAvailableBooks([...availableBooks, name]);
                                        }
                                        setSelectedBook(name);
                                        localStorage.setItem('expense_book', name);
                                        setNewBookName("");
                                        setShowAddBook(false);
                                    }}>Create & Switch</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* EDIT/RENAME BOOK MODAL */}
            {showEditBook && (
                <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-sm modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg">
                            <div className="modal-header">
                                <h6 className="modal-title fw-bold">Rename Expense Book</h6>
                                <button className="btn-close" onClick={() => setShowEditBook(false)}></button>
                            </div>
                            <div className="modal-body p-3">
                                <label className="small text-muted mb-1">New Name for "{selectedBook}"</label>
                                <input
                                    className="form-control mb-3"
                                    placeholder="e.g. Factory Office"
                                    value={editBookName}
                                    onChange={e => setEditBookName(e.target.value)}
                                />
                                <div className="d-grid">
                                    <button className="btn btn-primary" onClick={handleRenameBook}>Apply Changes</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
